import { uid } from 'uid';

/**
 * Deep-copy product options (and related page groups) with fresh IDs.
 * Cross-references within the copied set are remapped; references to options
 * that are not part of the copy are cleared.
 */

const remapFormula = (formula, optionIdMap) => {
  if (!formula || typeof formula !== 'string') return formula;
  let result = formula;
  for (const [oldId, newId] of optionIdMap.entries()) {
    result = result.replace(new RegExp(`\\{${escapeRegExp(oldId)}\\}`, 'g'), `{${newId}}`);
  }
  return result;
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const remapDynamicBound = (bound, optionIdMap, choiceIdMap) => {
  if (!bound || typeof bound !== 'object') return bound;
  const newOptionId = bound.optionId ? optionIdMap.get(bound.optionId) : null;
  if (!newOptionId) return undefined;

  return {
    ...bound,
    optionId: newOptionId,
    map: Array.isArray(bound.map)
      ? bound.map((entry) => ({
          ...entry,
          value:
            entry?.value != null && choiceIdMap.has(String(entry.value))
              ? choiceIdMap.get(String(entry.value))
              : entry.value,
        }))
      : [],
  };
};

const remapConditionRules = (conditions, optionIdMap, choiceIdMap) => {
  if (!conditions || typeof conditions !== 'object') return conditions;

  // Choice-level conditions use attribute/operator/value — leave as-is
  if (Array.isArray(conditions.rules) && !conditions.groups) {
    return JSON.parse(JSON.stringify(conditions));
  }

  if (!Array.isArray(conditions.groups)) {
    return JSON.parse(JSON.stringify(conditions));
  }

  const groups = conditions.groups
    .map((group) => {
      const rules = (group.rules || [])
        .map((rule) => {
          if (!rule?.optionId) return null;
          const newOptionId = optionIdMap.get(rule.optionId);
          if (!newOptionId) return null;

          let value = rule.value;
          if (value != null && choiceIdMap.has(String(value))) {
            value = choiceIdMap.get(String(value));
          }

          return {
            ...rule,
            optionId: newOptionId,
            value,
          };
        })
        .filter(Boolean);

      if (!rules.length) return null;
      return { ...group, rules };
    })
    .filter(Boolean);

  if (!groups.length) return null;

  return {
    ...conditions,
    groups,
  };
};

const cloneChoice = (choice, choiceIdMap) => {
  const newId = `choice_${uid()}`;
  if (choice?.id) {
    choiceIdMap.set(String(choice.id), newId);
  }

  const cloned = JSON.parse(JSON.stringify(choice));
  cloned.id = newId;

  if (cloned.priceFormula) {
    // Formulas remapped later once optionIdMap is complete
  }

  return cloned;
};

const cloneOption = (option, optionIdMap, choiceIdMap) => {
  const newId = `option_${uid()}`;
  if (option?.id) {
    optionIdMap.set(option.id, newId);
  }

  const cloned = JSON.parse(JSON.stringify(option));
  cloned.id = newId;

  if (cloned.choiceConfig?.choices?.length) {
    cloned.choiceConfig.choices = cloned.choiceConfig.choices.map((choice) =>
      cloneChoice(choice, choiceIdMap)
    );
  }

  return cloned;
};

const remapOptionReferences = (option, optionIdMap, choiceIdMap) => {
  if (option.conditions) {
    option.conditions = remapConditionRules(option.conditions, optionIdMap, choiceIdMap);
  }

  if (option.numberConfig) {
    if (option.numberConfig.dynamicMin) {
      option.numberConfig.dynamicMin = remapDynamicBound(
        option.numberConfig.dynamicMin,
        optionIdMap,
        choiceIdMap
      );
      if (!option.numberConfig.dynamicMin) {
        delete option.numberConfig.dynamicMin;
      }
    }
    if (option.numberConfig.dynamicMax) {
      option.numberConfig.dynamicMax = remapDynamicBound(
        option.numberConfig.dynamicMax,
        optionIdMap,
        choiceIdMap
      );
      if (!option.numberConfig.dynamicMax) {
        delete option.numberConfig.dynamicMax;
      }
    }
    if (option.numberConfig.matrixConfig?.otherField) {
      const remapped = optionIdMap.get(option.numberConfig.matrixConfig.otherField);
      if (remapped) {
        option.numberConfig.matrixConfig.otherField = remapped;
      } else {
        option.numberConfig.matrixConfig.otherField = '';
      }
    }
    if (option.numberConfig.priceFormula) {
      option.numberConfig.priceFormula = remapFormula(
        option.numberConfig.priceFormula,
        optionIdMap
      );
    }
  }

  if (option.toggleConfig?.priceFormula) {
    option.toggleConfig.priceFormula = remapFormula(option.toggleConfig.priceFormula, optionIdMap);
  }

  if (option.choiceConfig?.choices?.length) {
    option.choiceConfig.choices = option.choiceConfig.choices.map((choice) => {
      if (choice.priceFormula) {
        choice.priceFormula = remapFormula(choice.priceFormula, optionIdMap);
      }
      if (choice.conditions) {
        choice.conditions = remapConditionRules(choice.conditions, optionIdMap, choiceIdMap);
      }
      return choice;
    });
  }

  return option;
};

/**
 * Resolve page groups for copied options.
 * Reuses an existing target group with the same name when possible.
 */
const resolvePageGroups = ({
  sourceOptions,
  sourceOptionGroups,
  existingOptionGroups,
  optionIdMap,
}) => {
  const sourceGroupById = new Map((sourceOptionGroups || []).map((g) => [g.id, g]));
  const existingByName = new Map(
    (existingOptionGroups || []).map((g) => [g.name.trim().toLowerCase(), g])
  );

  const newGroups = [];
  const groupIdMap = new Map(); // source group id → target group id

  const ensureGroup = (sourceGroupId) => {
    if (!sourceGroupId) return null;
    if (groupIdMap.has(sourceGroupId)) return groupIdMap.get(sourceGroupId);

    const sourceGroup = sourceGroupById.get(sourceGroupId);
    const name = sourceGroup?.name?.trim();
    if (!name) {
      groupIdMap.set(sourceGroupId, null);
      return null;
    }

    const existing = existingByName.get(name.toLowerCase());
    if (existing) {
      groupIdMap.set(sourceGroupId, existing.id);
      return existing.id;
    }

    const newGroup = {
      id: `group-${uid()}`,
      name,
    };
    newGroups.push(newGroup);
    existingByName.set(name.toLowerCase(), newGroup);
    groupIdMap.set(sourceGroupId, newGroup.id);
    return newGroup.id;
  };

  // Pre-create groups for all source options that have a pageGroupId
  sourceOptions.forEach((opt) => {
    if (opt.pageGroupId && optionIdMap.has(opt.id)) {
      ensureGroup(opt.pageGroupId);
    }
  });

  return { newGroups, groupIdMap };
};

/**
 * Copy selected options from a source product into a target product shape.
 *
 * @param {Object} params
 * @param {Array} params.sourceOptions - All options on the source product
 * @param {Array} [params.sourceOptionGroups] - Option groups on the source product
 * @param {string[]} params.selectedOptionIds - Option IDs to copy
 * @param {Array} [params.existingOptionGroups] - Groups already on the target product
 * @returns {{ options: Array, newOptionGroups: Array }}
 */
export function copyProductOptions({
  sourceOptions = [],
  sourceOptionGroups = [],
  selectedOptionIds = [],
  existingOptionGroups = [],
}) {
  const selectedSet = new Set(selectedOptionIds);
  const toCopy = (sourceOptions || []).filter((opt) => selectedSet.has(opt.id));

  if (!toCopy.length) {
    return { options: [], newOptionGroups: [] };
  }

  const optionIdMap = new Map();
  const choiceIdMap = new Map();

  // First pass: clone with new IDs (builds maps)
  const clonedOptions = toCopy.map((opt) => cloneOption(opt, optionIdMap, choiceIdMap));

  // Resolve page groups based on source options being copied
  const { newGroups, groupIdMap } = resolvePageGroups({
    sourceOptions: toCopy,
    sourceOptionGroups,
    existingOptionGroups,
    optionIdMap,
  });

  // Second pass: remap cross-references + pageGroupId
  const options = clonedOptions.map((opt, idx) => {
    const source = toCopy[idx];
    remapOptionReferences(opt, optionIdMap, choiceIdMap);

    if (source.pageGroupId) {
      const mappedGroupId = groupIdMap.get(source.pageGroupId);
      opt.pageGroupId = mappedGroupId || null;
    } else {
      opt.pageGroupId = null;
    }

    return opt;
  });

  return {
    options,
    newOptionGroups: newGroups,
  };
}

/**
 * Select all option IDs that belong to the given page group IDs,
 * plus any explicitly selected option IDs.
 */
export function resolveSelectedOptionIds({
  sourceOptions = [],
  selectedOptionIds = [],
  selectedGroupIds = [],
}) {
  const groupSet = new Set(selectedGroupIds);
  const optionSet = new Set(selectedOptionIds);

  (sourceOptions || []).forEach((opt) => {
    if (opt.pageGroupId && groupSet.has(opt.pageGroupId)) {
      optionSet.add(opt.id);
    }
  });

  return [...optionSet];
}

export default copyProductOptions;
